/**
 * Clerk wiring, kept in one place so the rest of the app never imports the SDK
 * conditionally.
 *
 * Clerk is OFF unless a publishable key is present. That is deliberate: the
 * backend defaults to AUTH_PROVIDER=legacy for the same reason, and a
 * half-configured deployment should keep working on the old path rather than
 * render a sign-in box wired to nothing.
 *
 * `getToken` is set by <ClerkTokenBridge> at runtime. Clerk exposes the session
 * token through a hook, and lib/api.js is a plain module that cannot call one —
 * so the component hands its getter down here once, and the axios interceptor
 * reads it. The alternative, calling Clerk's singleton directly from the
 * interceptor, reaches around React's lifecycle and returns a stale token
 * during sign-out.
 */

export const PUBLISHABLE_KEY =
  (process.env.REACT_APP_CLERK_PUBLISHABLE_KEY || "").trim();

export function clerkEnabled() {
  return Boolean(PUBLISHABLE_KEY);
}

let _getToken = null;

/** Called once by ClerkTokenBridge. */
export function setTokenGetter(fn) {
  _getToken = fn;
}

/**
 * The current Clerk session token, or null.
 *
 * Never throws: this sits on the request path, and a token lookup that fails
 * should degrade to an unauthenticated request the API answers with 401 —
 * which the app already handles — rather than breaking the call site.
 */
export async function getClerkToken() {
  if (!_getToken) return null;
  try {
    return (await _getToken()) || null;
  } catch {
    return null;
  }
}
